import { lazy, Suspense, useMemo } from "react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { MobileCTABar } from "@/components/layout/MobileCTABar";
import { SEOHead } from "@/components/layout/SEOHead";
import { PageMeta } from "@/components/layout/PageMeta";
import { Hero } from "@/components/home/Hero";
import { WhatsAppFloat } from "@/components/shared/WhatsAppFloat";
import { ConsultationPopup } from "@/components/shared/ConsultationPopup";
import { useLocale } from "@/contexts/LanguageContext";
import { homeFAQ } from "@/data/content";

const PracticeAreaGrid = lazy(() => import("@/components/home/PracticeAreaGrid").then(m => ({ default: m.PracticeAreaGrid })));
const InjuryHubBanner = lazy(() => import("@/components/home/InjuryHubBanner").then(m => ({ default: m.InjuryHubBanner })));
const FeaturedResults = lazy(() => import("@/components/home/FeaturedResults").then(m => ({ default: m.FeaturedResults })));
const WhyMartinez = lazy(() => import("@/components/home/WhyMartinez").then(m => ({ default: m.WhyMartinez })));
const HowItWorks = lazy(() => import("@/components/home/HowItWorks").then(m => ({ default: m.HowItWorks })));
const AttorneySpotlight = lazy(() => import("@/components/home/AttorneySpotlight").then(m => ({ default: m.AttorneySpotlight })));
const HomeFAQ = lazy(() => import("@/components/home/HomeFAQ").then(m => ({ default: m.HomeFAQ })));
const Locations = lazy(() => import("@/components/home/Locations").then(m => ({ default: m.Locations })));
const FinalCTA = lazy(() => import("@/components/home/FinalCTA").then(m => ({ default: m.FinalCTA })));

const Index = () => {
  const { locale } = useLocale();

  const jsonLd = useMemo(() => [
    {
      "@context": "https://schema.org",
      "@type": "LegalService",
      name: "Martinez Law LLC",
      url: "https://martinez.law",
      telephone: "+1-773-818-3020",
      email: "clients@martinez.law",
      description: "Bilingual litigation attorneys serving Chicago, Aurora, and Kankakee. Personal injury, business law, estate planning, family law, criminal defense, and more.",
      areaServed: [
        { "@type": "City", name: "Chicago" },
        { "@type": "City", name: "Aurora" },
        { "@type": "City", name: "Kankakee" },
      ],
      address: [
        { "@type": "PostalAddress", streetAddress: "4115 W 26th St", addressLocality: "Chicago", addressRegion: "IL", postalCode: "60623" },
        { "@type": "PostalAddress", streetAddress: "5 W Downer Place", addressLocality: "Aurora", addressRegion: "IL", postalCode: "60506" },
        { "@type": "PostalAddress", streetAddress: "256 S Washington St", addressLocality: "Kankakee", addressRegion: "IL", postalCode: "60901" },
      ],
      aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "150" },
      knowsLanguage: ["en", "es"],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: homeFAQ.map(faq => ({
        "@type": "Question",
        name: faq.q.en,
        acceptedAnswer: { "@type": "Answer", text: faq.a.en },
      })),
    },
  ], []);

  return (
    <>
      <PageMeta
        title={locale === "es"
          ? "Martinez Law LLC | Abogados Litigantes Bilingües en Chicago"
          : "Martinez Law LLC | Bilingual Litigation Attorneys in Chicago"
        }
        description={locale === "es"
          ? "Abogados litigantes bilingües sirviendo Chicago, Aurora y Kankakee. Lesiones personales, derecho comercial, planificación patrimonial, defensa criminal y más. Consulta gratuita."
          : "Bilingual litigation attorneys serving Chicago, Aurora, and Kankakee. Personal injury, business law, estate planning, criminal defense, and more. Free consultation."
        }
        canonicalPath={locale === "es" ? "/es" : "/"}
        jsonLd={jsonLd}
      />
      <SEOHead />
      <Header />
      <main id="main-content">
        <Hero />
        <Suspense fallback={null}>
          <InjuryHubBanner />
          <PracticeAreaGrid />
          <FeaturedResults />
          <WhyMartinez />
          <HowItWorks />
          <AttorneySpotlight />
          <HomeFAQ />
          <Locations />
          <FinalCTA />
        </Suspense>
      </main>
      <Footer />
      <MobileCTABar />
      <WhatsAppFloat />
      <ConsultationPopup />
    </>
  );
};

export default Index;
